<template>
  <div
    :class="cssClasses"
    :style="{width: tooltipWidth, paddingBottom: noPaddingBottom ? 'unset' : ''}"
    ref="tooltip"
    v-click-outside="onClose"
  >
    <div :class="$style.tooltipHeader">
      <div :class="[$style.tooltipTitle, 'heading--4']" v-if="title" v-dompurify-html="title"/>
      <button
        v-if="showCloseButton"
        @click="onClose"
      >
        <b-svg
          name="x"
          :height="22"
        />
      </button>
    </div>
    <div
      v-if="description"
      v-dompurify-html="description"
      class="b-pt-2"
    />
    <div v-if="$slots.content" :class="[description && 'padding-top-12']">
      <slot name="content"/>
    </div>
  </div>
</template>

<script>
import { TOOLTIP_POSITIONS } from '@/lib/consts.js'
import ClickOutside from '@/booksy-vue-components/directives/click-outside.js'
import makeThemeable from '@/booksy-vue-components/utils/make-themeable.js'

const THEME_TYPES = [
  'default',
  'purple',
]

const Themeable = makeThemeable(THEME_TYPES, 'theme')

export default {
  name: 'b-tooltip',

  mixins: [
    Themeable,
  ],

  computed: {
    cssClasses () {
      return [
        this.$style.tooltip,
        this.$style[`tooltip--${this.position}`],
        this.visible && this.$style.tooltipVisible,
        this.theme__cssClasses,
      ]
    },
  },

  props: {
    tooltipWidth: {
      type: String,
      default: '300px',
    },
    title: String,
    description: String,
    noPaddingBottom: Boolean,
    visible: Boolean,
    position: {
      type: String,
      default: 'right-center',
      validator: (value) => TOOLTIP_POSITIONS.includes(value),
    },
    showCloseButton: Boolean,
  },
  directives: {
    ClickOutside,
  },
  methods: {
    onClose () {
      if (this.visible) this.$emit('on-close')
    },
  },
}
</script>

<style lang="scss" module>
  @import '@/assets/stylesheets/typography';

  .theme {
    &--default {
      --bg-color: #{$color-white};
      --txt-color: #{$color-black};
    }

    &--purple {
      --bg-color: #{$color-purple};
      --txt-color: #{$color-white};
    }
  }
  .tooltip {
    padding: 1rem;
    border-radius: $border-radius;
    box-shadow: $box-shadow-dropdown;
    position: absolute;
    opacity: 0;
    visibility: hidden;
    @include size('14');
    z-index: 200;
    display: none;
    background-color: var(--bg-color);
    color: var(--txt-color);

    --default-border-style: solid;

    &:before {
      content: '';
      width: 0;
      height: 0;
      position: absolute;
    }

    &Header {
      justify-content: flex-end;
      display: flex;
      width: 100%;
    }

    &Title {
      line-height: 42px;
      flex: 1;
    }

    &Visible {
      opacity: 1;
      visibility: visible;
      display: block;
    }

     &--right-top {
      bottom: -10px;
      left: calc(100% + 22px); // 22px offset from before and spacing

      &:before {
        bottom: 0;
        left: -12px;
        transform: translate(0, -50%);
        border-style: var(--default-border-style);
        border-width: 10px 12px 10px 0;
        border-color: transparent var(--bg-color) transparent transparent;
      }
    }
    &--top-center {
      bottom: calc(100% + 17px);
      left: 50%;
      transform: translate(-50%, 0%);

      &:before {
        top: 100%;
        left: 50%;
        transform: translate(-50%, 0%);
        border-left: 10px solid transparent;
        border-right: 10px solid transparent;
        border-top: 10px solid var(--bg-color);
      }
    }
    &--bottom-center {
      top: calc(100% + 17px);
      left: 50%;
      transform: translate(-50%, 0%);

      &:before {
        bottom: 100%;
        right: 50%;
        transform: translate(50%, 0%);
        border-left: 10px solid transparent;
        border-right: 10px solid transparent;
        border-bottom: 10px solid var(--bg-color);
      }
    }
    &--bottom-left {
      top: calc(100% + 17px);
      left: calc(100% + 17px);
      transform: translate(-100%, 0%);

      &:before {
        bottom: 100%;
        right: 0;
        transform: translate(-100%, 0%);
        border-left: 10px solid transparent;
        border-right: 10px solid transparent;
        border-bottom: 10px solid var(--bg-color);
      }
    }
    &--top-left {
      bottom: calc(100% + 17px);
      left: calc(100% + 17px);
      transform: translate(-100%, 0%);

      &:before {
        top: 100%;
        right: 0;
        transform: translate(-100%, 0%);
        border-left: 10px solid transparent;
        border-right: 10px solid transparent;
        border-top: 10px solid var(--bg-color);
      }
    }
    &--top-right {
      bottom: calc(100% + 17px);
      left: calc(-100%);

      &:before {
        top: 100%;
        transform: translate(50%, 0%);
        border-left: 10px solid transparent;
        border-right: 10px solid transparent;
        border-top: 10px solid var(--bg-color);
      }
    }
    &--right-center {
      left: calc(100% + 17px); // 17px offset from before and spacing
      top: 0;
      transform: translate(0, calc(-50% + 10px));

      &:before {
        top: 50%;
        left: -12px;
        transform: translate(0, -50%);
        border-style: var(--default-border-style);
        border-width: 10px 12px 10px 0;
        border-color: transparent var(--bg-color) transparent transparent;
      }
    }
    &--left-center {
      right: calc(100% + 17px); // 17px offset from before and spacing
      top: 0;
      transform: translate(0, calc(-50% + 10px));

      &:before {
        top: 50%;
        right: -12px;
        transform: translate(0, -50%);
        border-style: var(--default-border-style);
        border-width: 10px 0 10px 12px;
        border-color: transparent transparent transparent var(--bg-color);
      }
    }
    &--left-bottom {
      top: -10px;
      right: calc(100% + 17px); // 17px offset from before and spacing

      &:before {
        top: 20px;
        right: -12px;
        transform: translate(0, -50%);
        border-style: var(--default-border-style);
        border-width: 10px 0 10px 12px;
        border-color: transparent transparent transparent var(--bg-color);
      }
    }

     &--left-top {
      bottom: -10px;
      right: calc(100% + 22px); // 40px offset from before and spacing

      &:before {
        bottom: 0px;
        right: -12px;
        transform: translate(0, -50%);
        border-style: var(--default-border-style);
        border-width:  10px 0 10px 12px;
        border-color: transparent transparent transparent var(--bg-color);
      }
    }
  }
</style>
